/**
 * What a composed track is: its band (parts, each on an instrument), its sections written in jianpu
 * (notation.ts), and the order they play in. Tracks are data in `music/tracks/<id>.ts`.
 */
import type { Key, Mode } from './theory';

/**
 * The voices. Plucked: 古筝 guzheng, 古琴 guqin, 琵琶 pipa. Held (one tone glides from note to
 * note within a phrase): 笛子 dizi, 箫 xiao, 二胡 erhu. `pad`: a soft 笙-like drone under the band.
 * `perc`: drums, wood, gongs and bells.
 */
export type Instrument = 'zheng' | 'qin' | 'pipa' | 'dizi' | 'xiao' | 'erhu' | 'pad' | 'perc';

export interface InstrumentInfo {
  readonly kind: 'pluck' | 'held' | 'pad' | 'perc';
  /** Playable MIDI range (inclusive). */
  readonly range: readonly [number, number];
  readonly thai: string;
}

export const INSTRUMENTS: Record<Instrument, InstrumentInfo> = {
  // A 21-string zheng in D: D2 … D6, and pressed notes a little above.
  zheng: { kind: 'pluck', range: [36, 90], thai: 'กู่เจิง' },
  // The qin's open strings start near C2; its harmonics reach high.
  qin: { kind: 'pluck', range: [33, 89], thai: 'กู่ฉิน' },
  pipa: { kind: 'pluck', range: [45, 93], thai: 'ผีผา' },
  dizi: { kind: 'held', range: [62, 96], thai: 'ขลุ่ยตี๋จื่อ' },
  xiao: { kind: 'held', range: [53, 86], thai: 'ขลุ่ยเซียว' },
  erhu: { kind: 'held', range: [55, 93], thai: 'เอ้อหู' },
  pad: { kind: 'pad', range: [26, 76], thai: 'เสียงพื้น' },
  perc: { kind: 'perc', range: [0, 127], thai: 'เครื่องตี' },
};

export interface PartDef {
  readonly inst: Instrument;
  /** Level of the part (1 = as written). */
  readonly gain?: number;
  /** −1 left … 1 right. */
  readonly pan?: number;
  /** Send to the hall, 0–1 (default 0.3). */
  readonly reverb?: number;
}

export interface SectionDef {
  /** Length in bars; every part's line must fill exactly bars × meter beats. */
  readonly bars: number;
  /** Tempo of this section, if not the track's. */
  readonly bpm?: number;
  /** Slows down across the section to bpm × rit by its end (an ending, a 散板 close). */
  readonly rit?: number;
  /** Each part's line in jianpu (notation.ts). A part left out rests. */
  readonly parts: Readonly<Record<string, string>>;
}

export interface TrackDef {
  readonly id: string;
  /** Title in Thai, and where the game plays it. */
  readonly title: string;
  readonly mood: string;
  readonly use: string;
  /** Place in listings (the review page, the studio). */
  readonly order: number;
  /** 1 = key; the mode names the tonic (and the review page lists both). */
  readonly key: Key;
  readonly mode: Mode;
  readonly bpm: number;
  /** Beats in a bar. */
  readonly meter: number;
  /** Seed for the small variations (timing, touch, ornaments). Same seed, same performance. */
  readonly seed: number;
  /** How loose the playing is, 0 (strict) … 1. */
  readonly humanize?: number;
  /** Overall level of the track. */
  readonly gain?: number;
  readonly parts: Readonly<Record<string, PartDef>>;
  readonly sections: Readonly<Record<string, SectionDef>>;
  /** Played once before the loop. */
  readonly intro?: readonly string[];
  /** The order of the sections; it loops back to the start when `loop`. */
  readonly form: readonly string[];
  /** false: a stinger, played once to its end. */
  readonly loop: boolean;
}
